import { calculateExponentialProgress, calculateTotalProgress, getElapsedSeconds } from '../utils/exponentialProgress';
import { normalizeOptimizationDatasetNames } from '../utils/optimizationDatasetName';
import type { OptimizationState } from './optimizationStore';

/**
 * Persistence of the optimization store (AIT-423): schema version, migration of old
 * snapshots and the state fix-up after rehydration, as pure functions under test
 * instead of setters fired from eleven setTimeouts.
 */

export const OPTIMIZATION_STORE_VERSION = 1;

type Persisted = Record<string, unknown>;

/** Progress keys older snapshots kept; progress is recomputed, never restored. */
const LEGACY_PROGRESS_KEYS = [
  'optimizationProgress',
  'multiAgentOptimizationProgress',
  'connectorMultiAgentOptimizationProgress',
];

/** v0 → v1: drop persisted progress, normalize the dataset selection. */
export function migrateOptimizationStore(persisted: unknown, version: number): Persisted {
  const state: Persisted = persisted && typeof persisted === 'object' ? { ...(persisted as Persisted) } : {};
  if (version < 1) {
    LEGACY_PROGRESS_KEYS.forEach((key) => delete state[key]);
    const list = Array.isArray(state.selectedDatasets) ? (state.selectedDatasets as string[]) : [];
    const single = typeof state.selectedDataset === 'string' ? state.selectedDataset.trim() : '';
    if (list.length) {
      const normalized = normalizeOptimizationDatasetNames(list);
      state.selectedDatasets = normalized;
      state.selectedDataset = normalized[0] || single;
    } else if (single) {
      state.selectedDatasets = [single];
    }
  }
  return state;
}

type RehydrationInput = Pick<
  OptimizationState,
  | 'selectedDataset'
  | 'selectedDatasets'
  | 'isOptimizing'
  | 'isOptimized'
  | 'progressStartTime'
  | 'expectedIterationTime'
  | 'currentProgressStage'
  | 'iterations'
  | 'singleAgentChatMessages'
  | 'singleAgentChatInputs'
  | 'multiAgentIsOptimizing'
  | 'multiAgentIsOptimized'
  | 'multiAgentProgressStartTime'
  | 'multiAgentExpectedIterationTime'
  | 'multiAgentCurrentProgressStage'
  | 'multiAgentIterations'
  | 'multiAgentChatMessages'
  | 'multiAgentChatInputs'
  | 'connectorMultiAgentIsOptimizing'
  | 'connectorMultiAgentIsOptimized'
  | 'connectorMultiAgentChatMessages'
  | 'connectorMultiAgentChatInputs'
  | 'connectorMultiAgentIterationResults'
>;

const progressAt = (start: number, expected: number, stage: number, total: number) =>
  calculateTotalProgress(stage, total, calculateExponentialProgress(getElapsedSeconds(start), expected));

/**
 * What to change once the store is rehydrated: progress recomputed from elapsed time
 * (or zeroed), "after optimization" chats cleared for runs that did not finish, and the
 * connector run reset unless a Jobs API job id is persisted (polling owns it then).
 */
export function rehydrationPatch(
  state: RehydrationInput,
  opts: { hasPersistedOptimizationJob: boolean }
): Partial<OptimizationState> {
  const patch: Partial<OptimizationState> = {};

  if (!state.selectedDatasets?.length && state.selectedDataset?.trim()) {
    patch.selectedDatasets = [state.selectedDataset.trim()];
  } else if (state.selectedDatasets?.length) {
    patch.selectedDatasets = normalizeOptimizationDatasetNames(state.selectedDatasets);
    patch.selectedDataset = patch.selectedDatasets[0] || state.selectedDataset || '';
  }

  patch.optimizationProgress =
    state.isOptimizing && state.progressStartTime !== null
      ? progressAt(state.progressStartTime, state.expectedIterationTime, state.currentProgressStage, state.iterations)
      : 0;

  patch.multiAgentOptimizationProgress =
    state.multiAgentIsOptimizing && state.multiAgentProgressStartTime !== null
      ? progressAt(
          state.multiAgentProgressStartTime,
          state.multiAgentExpectedIterationTime,
          state.multiAgentCurrentProgressStage,
          state.multiAgentIterations
        )
      : 0;

  if (!state.isOptimized && (state.singleAgentChatMessages?.after?.length || state.singleAgentChatInputs?.after)) {
    patch.singleAgentChatMessages = { ...state.singleAgentChatMessages, after: [] };
    patch.singleAgentChatInputs = { ...state.singleAgentChatInputs, after: '' };
  }
  if (
    !state.multiAgentIsOptimized &&
    (state.multiAgentChatMessages?.after?.length || state.multiAgentChatInputs?.after)
  ) {
    patch.multiAgentChatMessages = { ...state.multiAgentChatMessages, after: [] };
    patch.multiAgentChatInputs = { ...state.multiAgentChatInputs, after: '' };
  }

  patch.connectorMultiAgentOptimizationProgress = 0;
  if (opts.hasPersistedOptimizationJob) {
    patch.connectorMultiAgentProgressStartTime = null;
  } else if (state.connectorMultiAgentIsOptimizing) {
    patch.connectorMultiAgentIsOptimizing = false;
    patch.connectorMultiAgentProgressStartTime = null;
    patch.connectorMultiAgentCurrentProgressStage = 0;
  }

  if (
    !state.connectorMultiAgentIsOptimized &&
    (state.connectorMultiAgentChatMessages?.after?.length || state.connectorMultiAgentChatInputs?.after)
  ) {
    patch.connectorMultiAgentChatMessages = { ...state.connectorMultiAgentChatMessages, after: [] };
    patch.connectorMultiAgentChatInputs = { ...state.connectorMultiAgentChatInputs, after: '' };
  }

  if (!state.connectorMultiAgentIsOptimizing && state.connectorMultiAgentIterationResults?.length > 0) {
    patch.connectorMultiAgentIterationResults = [];
    patch.connectorMultiAgentOptimizedPrompts = [];
    patch.connectorMultiAgentIsOptimized = false;
    patch.connectorMultiAgentSelectedIterationIndex = 0;
  }

  return patch;
}
